import { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, FileText, Newspaper, UserRound, UserRoundCheck, Building2, MapPin, Plus, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const STAT_CARDS = [
  { key: "residents", label: "Total Warga", icon: Users, color: "#064E3B", bg: "#D1FAE5" },
  { key: "male", label: "Laki-laki", icon: UserRound, color: "#0284C7", bg: "#E0F2FE" },
  { key: "female", label: "Perempuan", icon: UserRoundCheck, color: "#DB2777", bg: "#FCE7F3" },
  { key: "letters", label: "Surat Terbit", icon: FileText, color: "#D97706", bg: "#FEF3C7" },
];

export default function Dashboard() {
  const { profile } = useOutletContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ residents: 0, male: 0, female: 0, letters: 0, news: 0 });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="relative overflow-hidden rounded-2xl bg-[#064E3B] p-6 sm:p-8 text-white"
        data-testid="dashboard-header"
      >
        <div className="absolute right-0 top-0 h-full w-1/2 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
          {profile?.regency_logo_url ? (
            <img src={profile.regency_logo_url} alt="Logo Kabupaten" className="h-20 w-20 object-contain bg-white/10 rounded-xl p-2" data-testid="dashboard-regency-logo" />
          ) : (
            <div className="h-20 w-20 rounded-xl bg-white/10 grid place-items-center"><Building2 size={32} /></div>
          )}
          <div>
            <div className="text-emerald-200/80 text-sm font-medium">Pemerintah Desa</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Desa {profile?.village_name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-emerald-100/90 text-sm">
              <span className="flex items-center gap-1"><MapPin size={14} /> Kec. {profile?.district_name}</span>
              <span>{profile?.regency_name}</span>
              <span>{profile?.province_name}</span>
            </div>
            {profile?.village_head_name && (
              <div className="mt-2 text-sm text-emerald-200/70">Kepala Desa: {profile.village_head_name}</div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map((c, i) => (
          <motion.div
            key={c.key}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
            className="bg-white rounded-2xl border border-slate-200 p-5"
            data-testid={`stat-${c.key}`}
          >
            <div className="h-11 w-11 rounded-xl grid place-items-center mb-4" style={{ background: c.bg }}>
              <c.icon size={22} style={{ color: c.color }} />
            </div>
            <div className="font-heading text-3xl font-extrabold text-slate-900">{stats[c.key]}</div>
            <div className="text-sm text-slate-500 mt-1">{c.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Aksi Cepat</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <QuickAction icon={Plus} label="Tambah Warga" desc="Daftarkan penduduk baru" onClick={() => navigate("/warga")} color="#064E3B" testid="quick-add-resident" />
          <QuickAction icon={FileText} label="Buat Surat" desc="Terbitkan surat administrasi" onClick={() => navigate("/surat")} color="#D97706" testid="quick-create-letter" />
          <QuickAction icon={Newspaper} label="Buat Pengumuman" desc="Publikasikan warta desa" onClick={() => navigate("/berita")} color="#0284C7" testid="quick-create-news" />
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, desc, onClick, color, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className="group text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#064E3B] hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between">
        <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${color}18` }}>
          <Icon size={22} style={{ color }} />
        </div>
        <ArrowRight size={18} className="text-slate-300 group-hover:text-[#064E3B] group-hover:translate-x-1 transition-all" />
      </div>
      <div className="font-heading font-semibold text-slate-900 mt-4">{label}</div>
      <div className="text-sm text-slate-500">{desc}</div>
    </button>
  );
}
